import React, { useCallback, useEffect, useState } from "react";
import { Drawer, Input, InputNumber, Select, Switch, message } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "../../../../core/data/redux/store";
import {
    createTdsTaxSection,
    fetchTdsTaxSections,
    updateTdsTaxSection,
} from "../../../../core/data/redux/payroll/TDS/tdsTaxSection/tdsTaxSectionActions";
import { fetchFinancialYearsByCompanyId } from "../../../../core/data/redux/financialYear/financialYearActions";

const TaxSectionModal: React.FC<any> = ({
    modalType,
    handleCancel,
    isModalVisible,
    selectedRowData,
}) => {
    const dispatch = useDispatch<AppDispatch>();
    const { user } = useSelector((state: any) => state.auth);
    const { loading } = useSelector((state: any) => state.TDS.tdsTaxSection);
    const { activeCompany } = useSelector((state: any) => state.companyDetails);
    const { financialYearsByCompany } = useSelector((state: any) => state.financialYears);
    const companyId = activeCompany?.company_detail_id

    const [formData, setFormData] = useState({
        SectionDescription: "",
        OverallSectionLimit: 0,
        RequiresProof: 0,
        FinancialYearID: null as number | null,
        // ApplicableFinancialYearID: "",
        TaxRegimeApplicability: "Old Regime",
        Status: "active",
    });

    const [errors, setErrors] = useState<any>({});

    const resetForm = useCallback(() => {
        setFormData({
            SectionDescription: "",
            OverallSectionLimit: 0,
            RequiresProof: 0,
            FinancialYearID: null as number | null,
            TaxRegimeApplicability: "Old Regime",
            Status: "active",
        });
        setErrors({});
    }, []);


    useEffect(() => {
        if (companyId) {
            dispatch(fetchFinancialYearsByCompanyId(companyId))
        }
    }, [dispatch, companyId])

    useEffect(() => {
        if (modalType === "edit" && selectedRowData) {
            setFormData({
                SectionDescription: selectedRowData.SectionDescription || "",
                OverallSectionLimit: selectedRowData.OverallSectionLimit || 0,
                RequiresProof: selectedRowData.RequiresProof || 0,
                FinancialYearID: selectedRowData.ApplicableFinancialYearID != null ? Number(selectedRowData.ApplicableFinancialYearID) : null,
                TaxRegimeApplicability: selectedRowData.TaxRegimeApplicability || "Old Regime",
                Status: selectedRowData.Status || "active",
            });
        } else {
            resetForm();
        }
    }, [modalType, selectedRowData, resetForm]);

    const handleChange = (field: string, value: any) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
        setErrors((prev: any) => ({ ...prev, [field]: "" }));
    };

    const validateForm = () => {
        const newErrors: any = {};
        let valid = true;

        if (!formData.SectionDescription.trim()) {
            newErrors.SectionDescription = "Section description is required.";
            valid = false;
        }

        if (!formData.FinancialYearID) {
            newErrors.FinancialYearID = "Financial year is required.";
            valid = false;
        }

        if (formData.OverallSectionLimit < 0) {
            newErrors.OverallSectionLimit = "Limit cannot be negative.";
            valid = false;
        }

        setErrors(newErrors);
        return valid;
    };

    const handleSubmit = async () => {
        if (!validateForm()) return;

        const payload = {
            SectionDescription: formData.SectionDescription,
            OverallSectionLimit: formData.OverallSectionLimit,
            RequiresProof: formData.RequiresProof ? 1 : 0,
            ApplicableFinancialYearID: formData.FinancialYearID,
            TaxRegimeApplicability: formData.TaxRegimeApplicability,
            Status: formData.Status,
            ...(modalType === "edit"
                ? { ModifiedBy: user?.UserId || 1 }
                : { CreatedBy: user?.UserId || 1 }),
        };

        try {
            if (modalType === "edit") {
                await dispatch(
                    updateTdsTaxSection({ id: selectedRowData?.TaxSectionID, ...payload })
                ).unwrap();
                message.success("Tax section updated successfully");
            } else {
                await dispatch(createTdsTaxSection(payload)).unwrap();
                message.success("Tax section created successfully");
            }
            handleCancel();
            resetForm();
            dispatch(fetchTdsTaxSections());
        } catch {
            message.error("Something went wrong.");
        }
    };

    return (
        <Drawer
            title={modalType === "edit" ? "Edit Tax Section" : "Add Tax Section"}
            placement="right"
            width={520}
            onClose={() => {
                handleCancel();
                resetForm();
            }}
            visible={isModalVisible}
        >
            <div className="mb-3">
                <label className="form-label">Section Description<span style={{ color: "red" }}>*</span></label>
                <Input
                    value={formData.SectionDescription}
                    onChange={(e) => handleChange("SectionDescription", e.target.value)}
                />
                {errors.SectionDescription && <div className="text-danger">{errors.SectionDescription}</div>}
            </div>

            <div className="mb-3">
                <label className="form-label">Overall Section Limit</label>
                <InputNumber
                    value={formData.OverallSectionLimit}
                    min={0}
                    onChange={(value) => handleChange("OverallSectionLimit", value)}
                    style={{ width: "100%" }}
                />
                {errors.OverallSectionLimit && <div className="text-danger">{errors.OverallSectionLimit}</div>}
            </div>

            <div className="mb-3">
                <label className="form-label">
                    Financial Year <span style={{ color: 'red' }}>*</span>
                </label>
                <Select
                    value={formData.FinancialYearID}
                    onChange={(val) => handleChange("FinancialYearID", val)}
                    className="w-100"
                    placeholder="Select financial year"
                    allowClear
                    options={
                        (financialYearsByCompany || []).map((fy: any) => ({
                            value: fy.id,
                            label: fy.name,
                        }))
                    }
                />
                {errors.FinancialYearID && (
                    <div className="text-danger">{errors.FinancialYearID}</div>
                )}
            </div>

            <div className="mb-3">
                <label className="form-label">Tax Regime Applicability</label>
                <Select
                    value={formData.TaxRegimeApplicability}
                    onChange={(value) => handleChange("TaxRegimeApplicability", value)}
                    className="w-100"
                >
                    <Select.Option value="Old Regime">Old Regime</Select.Option>
                    <Select.Option value="New Regime">New Regime</Select.Option>
                </Select>
            </div>

            <div className="mb-3">
                <label className="form-label">Status</label>
                <Select
                    value={formData.Status}
                    onChange={(value) => handleChange("Status", value)}
                    className="w-100"
                >
                    <Select.Option value="active">Active</Select.Option>
                    <Select.Option value="inactive">Inactive</Select.Option>
                </Select>
            </div>

            <div className="mb-3">
                <label className="form-label me-2">Requires Proof</label>
                <Switch
                    checked={!!formData.RequiresProof}
                    onChange={(val) => handleChange("RequiresProof", val ? 1 : 0)}
                />
            </div>

            <div className="modal-footer mt-4">
                <button className="btn btn-secondary me-2" onClick={handleCancel}>
                    Cancel
                </button>
                <button
                    className="btn btn-primary"
                    onClick={handleSubmit}
                    disabled={loading}
                >
                    {loading && <span className="spinner-border spinner-border-sm me-2" />}
                    {modalType === "edit" ? "Update Section" : "Create Section"}
                </button>
            </div>
        </Drawer>
    );
};

export default TaxSectionModal;
